import { produce } from 'immer'
import * as R from 'ramda'
import { useCallback, useEffect, useMemo } from 'react'
import React from 'react'
import { useContextSelector } from 'use-context-selector'

import { FormContext } from 'components/formKit/Form'
import type { IFieldConfig, IFieldChange } from 'components/formKit/types'
import { EMPTY_OBJECT, IDENTITY } from 'constants/commonConstans'

import FieldLayout from '../FieldLayout'
import { renderInfo, renderLabel, renderDescription } from '../renderFieldBase'
import { useConfig } from '../useConfig'
import { useFormFieldValues } from '../useFormFieldValues'
import { validateRules } from '../validateRules'

interface IFieldProps {
  // path?: string // TODO? or spread config?
  config: IFieldConfig
}

const FormField: React.FC<IFieldProps> = ({ config: initialConfig }) => {
  const formSize = useContextSelector(FormContext, ({ size }) => size)
  const formDisabled = useContextSelector(FormContext, ({ disabled }) => disabled)
  const formLayout = useContextSelector(FormContext, ({ layout }) => layout)
  const meta = useContextSelector(FormContext, (c) => c.metas[initialConfig.name]) || EMPTY_OBJECT
  const onChangeValue = useContextSelector(FormContext, (c) => c.onChangeValue)
  const onChangeMeta = useContextSelector(FormContext, (c) => c.onChangeMeta)
  const onFieldBlur = useContextSelector(FormContext, (c) => c.onFieldBlur)
  const onFieldFocus = useContextSelector(FormContext, (c) => c.onFieldFocus)
  const registerFormField = useContextSelector(FormContext, (c) => c.registerFormField)
  const unregisterFormField = useContextSelector(FormContext, (c) => c.unregisterFormField)

  const config = useConfig(initialConfig)

  const { as: Control, effect = IDENTITY, setter = IDENTITY } = config
  const { name, readOnly, props, rules, hidden, layoutModifiers, ...rest } = config
  const { disabled: configDisabled, size: configSize, layout: configLayout } = rest
  const required = layoutModifiers?.includes('required')
  const label = useMemo(() => renderLabel(config), [config])
  const info = useMemo(() => renderInfo(config), [config])
  const description = useMemo(() => renderDescription(config), [config])
  const disabled = configDisabled || formDisabled
  const layout = configLayout || formLayout
  const size = configSize || formSize
  const error = meta.commonError || (meta.touched && meta.error)

  const { defaultValue, initialValue, value } = useFormFieldValues(config)

  const handleChange = useCallback(
    async (fieldChange: IFieldChange) => {
      const data = await effect(fieldChange)
      const changes = (Array.isArray(data) ? data : [data]).map((change) => {
        const { value, name } = change
        const newMeta = produce(meta, (draft) => {
          draft.dirty = !R.equals(value, initialValue)
          draft.error = !hidden && validateRules(value, rules)
          draft.commonError = false
          draft.touched = true
        })
        return { name, value: setter(value), meta: newMeta }
      })

      onChangeValue(changes)
    },
    [effect, onChangeValue, setter, meta, hidden, rules, initialValue],
  )

  const handleBlur = useCallback(() => onFieldBlur?.(name), [onFieldBlur, name])

  const handleFocus = useCallback(() => {
    onFieldFocus?.(name)
    onChangeMeta(name, { touched: true })
  }, [onChangeMeta, onFieldFocus, name])

  useEffect(() => {
    if (meta.touched) {
      const error = !hidden && validateRules(value, rules)
      onChangeMeta(name, { error })
    }
  }, [onChangeMeta, hidden, name, rules, value, meta.touched])

  useEffect(() => {
    registerFormField(name, config)

    return () => {
      unregisterFormField(name)
    }
  }, [registerFormField, unregisterFormField, name, config])

  if (hidden) {
    return null
  }

  return (
    <FieldLayout
      className={config.className}
      Component={config.layoutRender}
      description={description}
      error={error}
      info={info}
      label={label}
      layout={layout}
      layoutModifiers={layoutModifiers}
      name={name}
      required={required}
      size={size}
    >
      <Control
        {...props}
        defaultValue={defaultValue}
        disabled={disabled}
        error={Boolean(error)}
        name={name}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        readOnly={readOnly}
        size={size}
        value={value}
      />
    </FieldLayout>
  )
}

export default React.memo(FormField) as typeof FormField
